import React, { useEffect, useState } from 'react';
import { Activity, CheckCircle2, XCircle, RefreshCw, Plug, Settings2, ChevronRight, Send, Eye, EyeOff } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Drawer, Select } from '@/components/admin/shared';

const ROLE_STYLE = {
  primary: { bg: 'rgba(25,194,142,0.14)', ring: 'var(--pg-emerald)', text: 'var(--pg-emerald-2)', label: 'PRIMARY' },
  legacy:  { bg: 'rgba(226,199,122,0.14)', ring: 'var(--pg-gold-2)', text: 'var(--pg-gold-2)', label: 'LEGACY' },
};

function StatusPill({ ok }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest px-2 py-1 rounded-full border ${ok ? 'text-[var(--pg-emerald-2)] border-[var(--pg-emerald)] bg-[rgba(25,194,142,0.12)]' : 'text-[var(--pg-neon-pink)] border-[var(--pg-neon-pink)] bg-[rgba(255,62,165,0.08)]'}`}>
      {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
      {ok ? 'CONNECTED' : 'DISCONNECTED'}
    </span>
  );
}

export default function IntegrationCenter() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [creds, setCreds] = useState({});
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [tab, setTab] = useState('providers'); // providers | notifications | printful
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pfProducts, setPfProducts] = useState({ loading: false, count: null, items: [], error: null });

  const load = async () => {
    setLoading(true);
    try {
      const [p, e, t] = await Promise.all([
        api.get('/admin/integrations'),
        api.get('/admin/notifications/events'),
        api.get('/admin/notifications/templates').catch(() => ({ data: [] })),
      ]);
      setProviders(p.data);
      setEvents(e.data);
      setTemplates(t.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = (p) => {
    setDetail(p);
    // Preload existing (already-masked) values so admin can either replace or keep them.
    setCreds({ ...(p.credentials_masked || {}) });
    setTestResult(null);
  };

  const saveProvider = async () => {
    if (!detail) return;
    setSavingId(detail.provider);
    setMessage(null);
    try {
      const clean = {};
      for (const c of detail.credentials_schema) {
        const v = creds[c.key];
        // Skip masked placeholders so backend keeps prior value.
        if (v == null) continue;
        if (c.secret && typeof v === 'string' && v.includes('…')) continue;
        if (v === '' && detail.credentials_masked?.[c.key]) continue;
        clean[c.key] = v;
      }
      await api.put(`/admin/integrations/${detail.provider}`, {
        credentials: clean,
        config: detail.config || {},
        enabled: !!detail.enabled,
        webhook_url: detail.webhook_url || null,
      });
      setMessage('Saved.');
      await load();
      setDetail(null);
    } catch (e) {
      setMessage(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const testProvider = async (provider) => {
    setTesting(provider);
    setTestResult(null);
    try {
      const { data } = await api.post(`/admin/integrations/${provider}/test`);
      setTestResult(data);
      await load();
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(null);
    }
  };

  const loadPrintfulProducts = async () => {
    setPfProducts({ loading: true, count: null, items: [], error: null });
    try {
      const { data } = await api.get('/admin/integrations/printful/products');
      setPfProducts({ loading: false, count: data.count ?? 0, items: (data.result || []).slice(0, 25), error: data.ok ? null : (data.error || 'unknown') });
    } catch (e) {
      setPfProducts({ loading: false, count: null, items: [], error: e?.message || 'error' });
    }
  };

  const [pfSync, setPfSync] = useState({ running: false, result: null, error: null });
  const runPrintfulSyncCatalog = async () => {
    if (!confirm('Sync Printful catalog into Planet G? New items become drafts (hidden from public) so you can review before publishing.')) return;
    setPfSync({ running: true, result: null, error: null });
    try {
      const { data } = await api.post('/admin/integrations/printful/sync-catalog');
      setPfSync({ running: false, result: data, error: null });
      setMessage(`Sync complete — ${data.summary}`);
      // Also refresh the list preview
      loadPrintfulProducts();
    } catch (e) {
      setPfSync({ running: false, result: null, error: e?.response?.data?.detail || 'Sync failed' });
    }
  };

  const saveTemplate = async (event_key, patch) => {
    const current = templates.find((t) => t.event_key === event_key) || {};
    const merged = { ...current, ...patch };
    try {
      const { data } = await api.put(`/admin/notifications/templates/${event_key}`, {
        enabled_email: !!merged.enabled_email,
        enabled_sms:   !!merged.enabled_sms,
        subject:       merged.subject || '',
        email_html:    merged.email_html || '',
        sms_body:      merged.sms_body || '',
      });
      setTemplates((prev) => {
        const rest = prev.filter((p) => p.event_key !== event_key);
        return [...rest, data].sort((a, b) => a.event_key.localeCompare(b.event_key));
      });
      setMessage(`Saved template: ${event_key}`);
    } catch (e) {
      setMessage(e?.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div data-testid="admin-integration-center">
      <div className="pg-admin-bar flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Plug size={16} className="text-[var(--pg-ink-muted)]" />
          <p className="text-sm text-[var(--pg-ink)] font-mono">
            {loading ? 'Loading…' : `${providers.length} integrations · ${templates.length} notification templates`}
          </p>
        </div>
        <button onClick={load} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="ic-refresh">
          <RefreshCw size={13} /> REFRESH
        </button>
      </div>

      <div className="flex items-center gap-1.5 mb-4" data-testid="ic-tabs">
        {[
          { k: 'providers',     l: 'Providers' },
          { k: 'notifications', l: 'Notifications' },
          { k: 'printful',      l: 'Printful Catalog' },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ic-tab-${t.k}`}
            className={`px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase ${tab === t.k ? 'bg-[rgba(255,62,165,0.12)] border-[var(--pg-neon-pink)] text-[var(--pg-neon-pink)]' : 'border-[var(--pg-border-2)] text-[var(--pg-ink-muted)]'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'providers' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ic-provider-grid">
          {providers.map((p) => {
            const roleStyle = ROLE_STYLE[p.role] || ROLE_STYLE.primary;
            return (
              <div key={p.provider} className="pg-card p-5 flex flex-col gap-3" data-testid={`ic-provider-${p.provider}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-display text-xl tracking-[0.04em]">{p.display_name}</p>
                      <span className="text-[9px] font-mono tracking-widest uppercase px-1.5 py-0.5 rounded-full border" style={{ background: roleStyle.bg, borderColor: roleStyle.ring, color: roleStyle.text }}>{roleStyle.label}</span>
                    </div>
                    <p className="text-xs text-[var(--pg-ink-muted)] mt-1">{p.category}</p>
                  </div>
                  <StatusPill ok={p.health?.connected} />
                </div>
                <div className="text-xs text-[var(--pg-ink-muted)] font-mono space-y-1">
                  <p>Last success: {p.last_success_at ? new Date(p.last_success_at).toLocaleString() : '—'}</p>
                  {p.last_error_message && <p className="text-[var(--pg-neon-pink)] break-words">Last error: {p.last_error_message}</p>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => openDetail(p)} data-testid={`ic-config-${p.provider}`} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"><Settings2 size={13} /> Configure</button>
                  <button onClick={() => testProvider(p.provider)} disabled={testing === p.provider} data-testid={`ic-test-${p.provider}`} className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60">
                    <Activity size={13} /> {testing === p.provider ? 'Testing…' : 'Test'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'notifications' && (
        <div className="space-y-3" data-testid="ic-notifications">
          <p className="text-xs text-[var(--pg-ink-muted)]">Templates fire via <span className="text-[var(--pg-emerald-2)] font-mono">Brevo</span> (email + SMS). Placeholders like <code>{'{{name}}'}</code>, <code>{'{{order_id}}'}</code>, <code>{'{{tracking_url}}'}</code> are substituted at send time.</p>
          {events.map((e) => {
            const tpl = templates.find((t) => t.event_key === e.key) || { event_key: e.key, enabled_email: true, enabled_sms: false, subject: '', email_html: '', sms_body: '' };
            return (
              <details key={e.key} className="pg-card p-4" data-testid={`ic-template-${e.key}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs tracking-widest uppercase text-[var(--pg-ink)]">{e.label}</p>
                    <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono">{e.key}</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span className={tpl.enabled_email ? 'text-[var(--pg-emerald-2)]' : 'text-[var(--pg-ink-faint)]'}>EMAIL</span>
                    <span className="text-[var(--pg-ink-faint)]">·</span>
                    <span className={tpl.enabled_sms ? 'text-[var(--pg-emerald-2)]' : 'text-[var(--pg-ink-faint)]'}>SMS</span>
                  </div>
                </summary>
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Toggle label="Send email" value={tpl.enabled_email} onChange={(v) => saveTemplate(e.key, { ...tpl, enabled_email: v })} />
                    <Toggle label="Send SMS" value={tpl.enabled_sms} onChange={(v) => saveTemplate(e.key, { ...tpl, enabled_sms: v })} />
                  </div>
                  <TextField label="Subject" value={tpl.subject || ''} onChange={(v) => saveTemplate(e.key, { ...tpl, subject: v })} />
                  <TextField label="Email HTML body" value={tpl.email_html || ''} onChange={(v) => saveTemplate(e.key, { ...tpl, email_html: v })} textarea rows={6} />
                  <TextField label="SMS body (≤160 chars recommended)" value={tpl.sms_body || ''} onChange={(v) => saveTemplate(e.key, { ...tpl, sms_body: v })} textarea rows={2} />
                </div>
              </details>
            );
          })}
        </div>
      )}

      {tab === 'printful' && (() => {
        const printfulProvider = providers.find((p) => p.provider === 'printful');
        const printfulConnected = !!(printfulProvider && printfulProvider.health && printfulProvider.health.connected);
        // Find the most recent successful catalog-sync history entry (if any).
        const lastCatalogSync = ((printfulProvider && printfulProvider.sync_history) || [])
          .slice()
          .reverse()
          .find((h) => h && h.ok && (h.action === 'sync_catalog'));
        const lastSuccessAt = (lastCatalogSync && lastCatalogSync.at) || printfulProvider?.last_success_at || null;
        return (
        <div className="pg-card p-5" data-testid="ic-printful-panel">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <p className="pg-label">PRINTFUL CATALOG</p>
              {lastSuccessAt && (
                <p className="text-[10px] font-mono tracking-widest text-[var(--pg-emerald-2)] mt-1" data-testid="ic-printful-last-sync">
                  LAST SUCCESSFUL SYNC · {new Date(lastSuccessAt).toLocaleString()}
                </p>
              )}
              {!printfulConnected && (
                <p className="text-[10px] font-mono tracking-widest text-[var(--pg-neon-pink)] mt-1" data-testid="ic-printful-disconnected">
                  Printful integration not connected. Configure it from the Providers tab to enable catalog sync.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={loadPrintfulProducts} disabled={pfProducts.loading || !printfulConnected} data-testid="ic-printful-load" className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60">
                <RefreshCw size={13} /> {pfProducts.loading ? 'Loading…' : 'List from Printful'}
              </button>
              {printfulConnected && (
                <button
                  onClick={runPrintfulSyncCatalog}
                  disabled={pfSync.running}
                  data-testid="ic-printful-sync-catalog"
                  title="Import & refresh Planet G products from Printful"
                  className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60"
                >
                  <RefreshCw size={13} className={pfSync.running ? 'animate-spin' : ''} /> {pfSync.running ? 'Syncing…' : 'Sync Catalog → Planet G'}
                </button>
              )}
            </div>
          </div>

          {pfSync.result && (
            <div className="pg-input rounded-[var(--pg-radius-sm)] p-3 mb-3 text-xs space-y-1" data-testid="ic-printful-sync-summary">
              <p className="font-mono tracking-widest text-[var(--pg-emerald-2)]">SYNC COMPLETE</p>
              <p className="text-[var(--pg-ink)]">Imported <strong>{pfSync.result.imported?.length || 0}</strong> · Updated <strong>{pfSync.result.updated?.length || 0}</strong> · Skipped <strong>{pfSync.result.skipped?.length || 0}</strong> · Errors <strong>{pfSync.result.errors?.length || 0}</strong></p>
              {pfSync.result.synced_at && (
                <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono">Synchronized at {new Date(pfSync.result.synced_at).toLocaleString()}</p>
              )}
              {(pfSync.result.imported?.length || 0) > 0 && (
                <p className="text-[10px] text-[var(--pg-ink-faint)]">New items were created as <strong>drafts</strong>. Publish them from the Vault tab when ready.</p>
              )}
              {(pfSync.result.errors?.length || 0) > 0 && (
                <ul className="text-[10px] text-[var(--pg-neon-pink)] font-mono list-disc pl-5">
                  {pfSync.result.errors.slice(0, 5).map((er, i) => (<li key={i}>#{String(er.item_id)}: {er.error}</li>))}
                </ul>
              )}
            </div>
          )}
          {pfSync.error && <p className="text-xs text-[var(--pg-neon-pink)] mb-3" data-testid="ic-printful-sync-error">{pfSync.error}</p>}

          {!printfulConnected ? (
            <p className="text-sm text-[var(--pg-ink-muted)]">Connect the Printful integration in the <span className="font-mono text-[var(--pg-ink)]">Providers</span> tab, then return here to import your catalog.</p>
          ) : pfProducts.count === null ? (
            <p className="text-sm text-[var(--pg-ink-muted)]">Press <span className="font-mono text-[var(--pg-ink)]">Sync Catalog → Planet G</span> to import your Printful store into the Vault. Existing CMS fields (description, publish status, tags, cover images you&apos;ve customized) are preserved.</p>
          ) : pfProducts.error ? (
            <p className="text-sm text-[var(--pg-neon-pink)]">{pfProducts.error}</p>
          ) : pfProducts.count === 0 ? (
            <div className="text-sm text-[var(--pg-ink-muted)] space-y-2">
              <p>No products in your Printful store yet.</p>
              <p className="text-[var(--pg-ink-faint)]">This is expected during ramp-up. Add products in Printful and press Sync — Planet G will automatically route future orders to fulfillment.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--pg-border-2)]">
              {pfProducts.items.map((it) => (
                <li key={it.id} className="py-3 flex items-center gap-3" data-testid={`ic-pf-product-${it.id}`}>
                  {it.thumbnail_url && <img src={it.thumbnail_url} alt="" className="w-10 h-10 rounded object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--pg-ink)] truncate">{it.name}</p>
                    <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono">variants: {it.variants} · sync_product_id: {it.id}</p>
                  </div>
                </li>
              ))}
              <li className="py-2 text-[10px] text-[var(--pg-ink-faint)] font-mono">Showing {pfProducts.items.length} of {pfProducts.count} · use Printful dashboard for full editing</li>
            </ul>
          )}
        </div>
        );
      })()}

      {message && <p className="text-xs font-mono tracking-widest text-[var(--pg-tertiary)] mt-3">{message}</p>}
      {testResult && (
        <div className={`mt-3 pg-card p-3 text-xs`} data-testid="ic-test-result">
          <p className={`font-mono tracking-widest ${testResult.ok ? 'text-[var(--pg-emerald-2)]' : 'text-[var(--pg-neon-pink)]'}`}>
            {testResult.ok ? 'PASS' : 'FAIL'}
          </p>
          <pre className="text-[10px] text-[var(--pg-ink-muted)] whitespace-pre-wrap break-words mt-1">{JSON.stringify(testResult, null, 2).slice(0, 400)}</pre>
        </div>
      )}

      {/* Provider detail drawer */}
      <Drawer open={!!detail} onClose={() => setDetail(null)} title={detail ? `Configure ${detail.display_name}` : ''}>
        {detail && (
          <div className="space-y-4">
            <p className="text-xs text-[var(--pg-ink-muted)]">Secrets are encrypted at rest. Leave a field with its <code>abcd…wxyz</code> preview unchanged to keep the current value.</p>
            <Toggle label={`Enabled site-wide`} value={!!detail.enabled} onChange={(v) => setDetail({ ...detail, enabled: v })} />
            {detail.credentials_schema.map((c) => (
              <TextField
                key={c.key}
                label={`${c.label}${c.optional ? ' (optional)' : ''}${c.help ? ` — ${c.help}` : ''}`}
                value={creds[c.key] ?? ''}
                onChange={(v) => setCreds({ ...creds, [c.key]: v })}
                placeholder={c.secret ? 'Enter secret…' : ''}
              />
            ))}
            <TextField label="Webhook URL (optional)" value={detail.webhook_url || ''} onChange={(v) => setDetail({ ...detail, webhook_url: v })} placeholder="https://…" />
            {detail.docs && <a href={detail.docs} target="_blank" rel="noreferrer" className="text-xs text-[var(--pg-emerald-2)] underline">Provider docs ↗</a>}
            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--pg-border-2)]">
              <button onClick={() => testProvider(detail.provider)} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"><Activity size={13} /> Test</button>
              <button onClick={saveProvider} disabled={savingId === detail.provider} data-testid="ic-save" className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-4 py-1.5 text-xs disabled:opacity-60">{savingId === detail.provider ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
